import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, Redirect } from "react-router-dom";
import axios from "axios";
import {
  FaBus,
  FaMapMarkedAlt,
  FaUsers,
  FaPlus,
  FaExchangeAlt,
  FaSyncAlt,
  FaRoute,
  FaRupeeSign,
  FaMapMarkerAlt,
  FaArrowRight,
  FaClipboardCheck,
  FaIdBadge,
  FaShieldAlt,
  FaUserPlus,
} from "react-icons/fa";
import MyLoader from "../Spinner/MyLoader";
import SessionManager from "../Utils/SessionManager";
import myToast from "../Utils/MyToast";
import BackButton from "../ChildComponent/BackButton";
import COLORS from "../resources/colors/COLORS";
import "../css/Admin_css/admin-customize.css";
import "../css/Admin_css/transport-dashboard.css";

const API_BASE = () =>
  `${process.env.REACT_APP_SERVER_NAME}${process.env.REACT_APP_TARGET_DIR}`;

const HERO_GRADIENT =
  "linear-gradient(135deg, #0ea5e9 0%, #2563eb 55%, #4f46e5 100%)";

const HUB_CARDS = [
  {
    to: "/AdminMain/TransportRoutes",
    title: "Routes",
    hint: "Manage routes & stops",
    icon: FaRoute,
    tone: "routes",
  },
  {
    to: "/AdminMain/TransportVehicles",
    title: "Vehicles",
    hint: "Fleet registry & details",
    icon: FaBus,
    tone: "vehicles",
  },
  {
    to: "/AdminMain/TransportStudent",
    title: "Students",
    hint: "Assign & view transport",
    icon: FaUsers,
    tone: "students",
  },
  {
    to: "/AdminMain/AddTransportStudentMultiple",
    title: "Add Multiple",
    hint: "Bulk assign transport",
    icon: FaUserPlus,
    tone: "multi",
  },
  {
    to: "/AdminMain/TransportRoutes",
    title: "Add Route",
    hint: "Create a new route",
    icon: FaPlus,
    tone: "add",
  },
  {
    to: "/AdminMain/PromoteTransportStudent",
    title: "Promote",
    hint: "Move to next session",
    icon: FaExchangeAlt,
    tone: "promote",
  },
  {
    to: "/AdminMain/ManageTransportFee",
    title: "Transport Fee",
    hint: "Distance slabs & rates",
    icon: FaRupeeSign,
    tone: "transport-fee",
  },
  {
    to: "/AdminMain/TransportAttendance",
    title: "Attendance",
    hint: "Bus student attendance",
    icon: FaClipboardCheck,
    tone: "attendance",
  },
  {
    to: "/AdminMain/DriverDetails",
    title: "Drivers",
    hint: "View transport drivers",
    icon: FaIdBadge,
    tone: "driver",
  },
  {
    to: "/AdminMain/TransportPermission",
    title: "Permissions",
    hint: "Transport staff access",
    icon: FaShieldAlt,
    tone: "permissions",
  },
];

function formatCount(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "0";
  return Math.round(n).toLocaleString("en-IN");
}

function buildFallbackDashboard(setupData, extras = {}) {
  const routes = Array.isArray(setupData?.routes) ? setupData.routes : [];
  const vehicles = Array.isArray(setupData?.VehicleList)
    ? setupData.VehicleList
    : [];
  const pickupPoints = Array.isArray(setupData?.PickupPoints)
    ? setupData.PickupPoints
    : [];
  const activeStudents = Number(extras.activeStudents);
  const totalMonthlyFee = Number(extras.totalMonthlyFee);

  const vehicleTypes = {};
  vehicles.forEach((vehicle) => {
    const type = String(vehicle?.vehicle_type || "Other").trim() || "Other";
    vehicleTypes[type] = (vehicleTypes[type] || 0) + 1;
  });

  return {
    stats: {
      route_count: routes.length,
      vehicle_count: vehicles.length,
      pickup_point_count: pickupPoints.length,
      active_students: Number.isFinite(activeStudents) ? activeStudents : 0,
      fee_slabs: 0,
      total_monthly_fee: Number.isFinite(totalMonthlyFee) ? totalMonthlyFee : 0,
    },
    routes: routes.map((route) => ({ ...route, student_count: 0 })),
    vehicles,
    pickup_points: pickupPoints,
    vehicle_types: vehicleTypes,
    session: Array.isArray(extras.session) ? extras.session : [],
  };
}

function parseApiPayload(res) {
  if (!res?.data) return null;
  if (typeof res.data === "object" && res.data.api_status) return res.data;
  if (typeof res.data === "string") {
    const trimmed = res.data.trim();
    if (!trimmed) return null;
    try {
      return JSON.parse(trimmed);
    } catch {
      return null;
    }
  }
  return res.data;
}

async function loadFallbackDashboard(schoolCode, session) {
  const setupRes = await axios.get(`${API_BASE()}getTransportSetupData.php`, {
    params: { school_code: schoolCode },
  });

  const setupPayload = parseApiPayload(setupRes);

  if (setupPayload?.api_status === "OK" && setupPayload?.data) {
    return buildFallbackDashboard(setupPayload.data, {
      session: Array.isArray(session) ? session : [],
    });
  }

  return null;
}

function StatCard({ tone, label, value, icon: Icon, sub }) {
  return (
    <div className={`tp-stat tp-stat--${tone} lift`}>
      <div className={`tp-stat__icon tp-stat__icon--${tone}`} aria-hidden>
        <Icon />
      </div>
      <div className="tp-stat__body">
        <div className="tp-stat__label">{label}</div>
        <div className="tp-stat__value">{value}</div>
        {sub ? <div className="tp-stat__sub">{sub}</div> : null}
      </div>
    </div>
  );
}

function HubCard({ to, title, hint, icon: Icon, tone, index = 0 }) {
  return (
    <Link
      to={to}
      className={`tp-hub-tile tp-hub-tile--${tone}`}
      aria-label={title}
      style={{ "--tp-hub-i": index }}
      title={hint}
    >
      <span
        className={`tp-hub-tile__circle tp-hub-tile__circle--${tone}`}
        aria-hidden
      >
        {Icon ? <Icon /> : null}
      </span>
      <span className="tp-hub-tile__content">
        <span className="tp-hub-tile__title">{title}</span>
        <span className="tp-hub-tile__hint">{hint}</span>
      </span>
    </Link>
  );
}

function RouteCard({ route }) {
  const studentCount = Number(route.student_count) || 0;
  const distance = route.route_distance ? `${route.route_distance} km` : null;

  return (
    <Link
      to={`/AdminMain/TransportRoutesDetails/${encodeURIComponent(
        String(route.route_id)
      )}`}
      className="tp-route-card tp-route-card--compact lift"
      aria-label={`View ${route.route_name || "route"} details`}
    >
      <span className="tp-route-card__icon" aria-hidden>
        <FaRoute />
      </span>
      <span className="tp-route-card__main">
        <span className="tp-route-card__name">
          {route.route_name || "Unnamed route"}
        </span>
        <span className="tp-route-card__path">
          {route.start_from || "—"} → {route.end_to || "—"}
          {distance ? ` · ${distance}` : ""}
        </span>
      </span>
      <span
        className={`tp-route-card__count${
          studentCount > 0
            ? " tp-route-card__count--active"
            : " tp-route-card__count--empty"
        }`}
      >
        {formatCount(studentCount)}
      </span>
      <span className="tp-route-card__arrow" aria-hidden>
        <FaArrowRight />
      </span>
    </Link>
  );
}

function Transport() {
  const loggedIn = localStorage.getItem("token") != null;
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);

  const loadDashboard = useCallback(async () => {
    const schoolCode = SessionManager.getSchoolCode();
    if (!schoolCode) {
      setDashboard(null);
      myToast.notify_warning("School code is missing.");
      return;
    }

    const session = SessionManager.getCurrentSession();
    let sessionId = "";
    if (Array.isArray(session) && session[0]?.session_id) {
      sessionId = String(session[0].session_id);
    } else if (session && typeof session === "object" && session.session_id) {
      sessionId = String(session.session_id);
    }

    setLoading(true);
    try {
      const dashUrl = `${API_BASE()}getTransportDashboard.php`;
      const params = { school_code: schoolCode };
      if (sessionId) params.session_id = sessionId;

      try {
        const res = await axios.get(dashUrl, { params });
        const dashPayload = parseApiPayload(res);
        if (dashPayload?.api_status === "OK" && dashPayload?.data) {
          setDashboard(dashPayload.data);
          return;
        }
      } catch (dashError) {
        console.warn(
          "[Transport] dashboard API unavailable, using fallback",
          dashError
        );
      }

      const fallbackDashboard = await loadFallbackDashboard(schoolCode, session);
      if (fallbackDashboard) {
        setDashboard(fallbackDashboard);
        return;
      }

      setDashboard(null);
    } catch (e) {
      console.error("[Transport] dashboard error", e);
      setDashboard(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  const stats = dashboard?.stats ?? {};
  const routes = Array.isArray(dashboard?.routes) ? dashboard.routes : [];
  const vehicleTypes = dashboard?.vehicle_types ?? {};
  const routeStudentTotal = useMemo(
    () =>
      routes.reduce((sum, route) => sum + (Number(route.student_count) || 0), 0),
    [routes]
  );
  const activeStudentCount = Math.max(
    Number(stats.active_students) || 0,
    routeStudentTotal
  );
  const sessionName =
    Array.isArray(dashboard?.session) && dashboard.session[0]?.session_name
      ? dashboard.session[0].session_name
      : Array.isArray(SessionManager.getCurrentSession()) &&
          SessionManager.getCurrentSession()[0]?.session_name
        ? SessionManager.getCurrentSession()[0].session_name
        : null;

  const routePreview = useMemo(() => routes.slice(0, 6), [routes]);

  if (!loggedIn) return <Redirect to="/Login" />;
  if (loading) return <MyLoader />;

  return (
    <div
      className="content payroll-page tp-page"
      style={{
        background: COLORS.white,
        fontFamily:
          'Inter, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      }}
    >
      <div
        className="content-header"
        style={{ background: COLORS.primary_color_shade5 }}
      >
        <div className="leftside-content-header">
          <ul className="breadcrumbs">
            <li>
              <i
                className="fa fa-home"
                aria-hidden="true"
                style={{ color: COLORS.white }}
              />
              <span style={{ color: COLORS.white }}> Transport</span>
            </li>
          </ul>
        </div>
        <div className="rightside-content-header">
          <BackButton />
        </div>
      </div>

      <div className="bg_page_white_rounded box_shadow animated fadeInUp tp-shell">
        <header className="tp-hero" style={{ "--tp-hero-bg": HERO_GRADIENT }}>
          <div className="tp-hero__brand">
            <div className="tp-hero__icon" aria-hidden>
              <FaBus />
            </div>
            <div>
              <h1 className="tp-hero__title">Transport Dashboard</h1>
              <p className="tp-hero__sub">
                {sessionName ? `${sessionName} · ` : ""}
                {formatCount(activeStudentCount)} students ·{" "}
                {formatCount(stats.route_count)} routes ·{" "}
                {formatCount(stats.pickup_point_count)} stops
              </p>
            </div>
          </div>
          <div className="tp-hero__tools">
            <button
              type="button"
              className={`pp-icon-btn ${loading ? "spinning" : ""}`}
              onClick={loadDashboard}
              disabled={loading}
              aria-label="Refresh transport data"
            >
              <FaSyncAlt aria-hidden />
            </button>
          </div>
        </header>

        <section className="tp-section" aria-label="Transport stats">
          <div className="tp-stats">
            <StatCard
              tone="routes"
              label="Active routes"
              value={formatCount(stats.route_count)}
              icon={FaMapMarkedAlt}
              sub="Configured routes"
            />
            <StatCard
              tone="fleet"
              label="Fleet vehicles"
              value={formatCount(stats.vehicle_count)}
              icon={FaBus}
              sub={
                Object.keys(vehicleTypes).length
                  ? Object.entries(vehicleTypes)
                      .map(([type, count]) => `${type}: ${count}`)
                      .join(" · ")
                  : "No type breakdown"
              }
            />
            <StatCard
              tone="students"
              label="Transport students"
              value={formatCount(activeStudentCount)}
              icon={FaUsers}
              sub="Active this session"
            />
            <StatCard
              tone="fee"
              label="Active Stops"
              value={formatCount(stats.pickup_point_count)}
              icon={FaMapMarkerAlt}
              sub="Pickup points"
            />
          </div>
        </section>

        <section className="tp-hub" aria-labelledby="tp-hub-heading">
          <div className="tp-hub__head">
            <h2 id="tp-hub-heading" className="tp-section-title">
              Transport modules
            </h2>
            <p className="tp-hub__sub">
              Quick access to routes, students, fees & more
            </p>
          </div>
          <div className="tp-hub-grid tp-hub-grid--tiles">
            {HUB_CARDS.map((card, index) => (
              <HubCard key={card.to} {...card} index={index} />
            ))}
          </div>
        </section>

        <section className="tp-section tp-section--last" aria-label="Routes">
          <div className="tp-section-bar">
            <h2 className="tp-section-title">Routes overview</h2>
            <Link to="/AdminMain/TransportRoutes" className="tp-section-link">
              View all routes
            </Link>
          </div>
          <div className="tp-route-grid">
            {routePreview.length === 0 ? (
              <div className="tp-empty tp-empty--wide">
                No routes configured yet.
              </div>
            ) : (
              routePreview.map((route) => (
                <RouteCard key={route.route_id} route={route} />
              ))
            )}
          </div>
        </section>
      </div>
    </div>
  );
}

export default Transport;
